import { Button, PinInput, Stack, Text } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { ApiError, officeApi } from '../api.js';
import { AuthScreen } from '../components/AuthScreen.js';
import { ErrorAlert } from '../components/ErrorAlert.js';
import { FormHeader } from '../components/FormHeader.js';
import { TotpQr, TotpSecret, useTotpQr } from '../components/TotpEnrollment.js';
import { pathForStage } from '../routing.js';
import { useSession } from '../session.js';

/**
 * Eski giriş aşaması `totp_enrollment_required` (ADR 0005): iki adımlı doğrulama artık isteğe
 * bağlı (sahip 2026-11-01), ama bu aşamada açık kalmış oturumlar için sayfa yerinde durur.
 */
export function TotpSetupPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const session = useSession();
  const [secret, setSecret] = useState<string | null>(null);
  const [otpauthUri, setOtpauthUri] = useState<string | null>(null);
  const qr = useTotpQr(otpauthUri);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  // Sır bir kez istenir: her istek yeni sır üretir (ADR 0005 §4)
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    officeApi
      .enrollTotp()
      .then(({ secret: value, otpauthUri: uri }) => {
        setSecret(value);
        setOtpauthUri(uri);
      })
      .catch((failure: unknown) =>
        setError(failure instanceof ApiError ? failure.code : 'GENERIC'),
      );
  }, []);

  const confirm = async (value: string) => {
    if (value.length !== 6) return;
    setBusy(true);
    setError(null);
    try {
      const { stage } = await officeApi.confirmTotp(value);
      session.setStage(stage);
      navigate(pathForStage(stage), { replace: true });
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.code : 'GENERIC');
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthScreen>
      <FormHeader
        eyebrow={t('totpSetup.step', { n: 2, total: 2 })}
        title={t('totpSetup.title')}
        subtitle={t('totpSetup.subtitle')}
      />
      <ErrorAlert code={error} />
      <Stack gap="lg">
        <TotpQr qr={qr} />
        {secret ? <TotpSecret secret={secret} /> : null}
        <div>
          <Text component="label" htmlFor="totp-code-1" size="sm" fw={500} mb={8} display="block">
            {t('totpSetup.code')}
          </Text>
          <PinInput
            id="totp-code"
            length={6}
            type="number"
            oneTimeCode
            size="md"
            value={code}
            onChange={setCode}
            onComplete={(value) => void confirm(value)}
            disabled={!secret || busy}
            getInputProps={(index) => ({ 'aria-label': t('totpSetup.digit', { n: index + 1 }) })}
          />
        </div>
        <Button
          size="md"
          fullWidth
          disabled={code.length !== 6}
          loading={busy}
          onClick={() => void confirm(code)}
        >
          {t('totpSetup.submit')}
        </Button>
      </Stack>
    </AuthScreen>
  );
}
